import { ServicePluginDefinition } from '@wix/sdk-types';
import {
  Context,
  GetPaymentSettingsRequest,
  GetPaymentSettingsResponse,
  GetPaymentSettingsForCheckoutRequest,
  GetPaymentSettingsForCheckoutResponse,
} from './service-plugins-types.js';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';

export interface GetPaymentSettingsEnvelope {
  request: GetPaymentSettingsRequest;
  metadata: Context;
}

export interface GetPaymentSettingsForCheckoutEnvelope {
  request: GetPaymentSettingsForCheckoutRequest;
  metadata: Context;
}

export const provideHandlers = ServicePluginDefinition<{
  /**
   *
   * This method retrieves payment settings from your app.
   *
   * Wix calls this method during the payment process. For example, when a customer inserts credit card details and places an order.
   * This method retrieves the payment settings to apply, and Wix passes on the settings to the payment provider. */
  getPaymentSettings(
    payload: GetPaymentSettingsEnvelope
  ): GetPaymentSettingsResponse | Promise<GetPaymentSettingsResponse>;

  /**
   * Returns payment settings for provided Checkout entity. */
  getPaymentSettingsForCheckout(
    payload: GetPaymentSettingsForCheckoutEnvelope
  ):
    | GetPaymentSettingsForCheckoutResponse
    | Promise<GetPaymentSettingsForCheckoutResponse>;
}>('ECOM_PAYMENT_SETTINGS', [
  {
    name: 'getPaymentSettings',
    primaryHttpMappingPath: '/v1/payment-settings',
    transformations: {
      toREST: (payload: any) => {
        const toRestResponse = payload;

        return renameKeysFromSDKRequestToRESTRequest(toRestResponse);
      },
      fromREST: (payload: any) => {
        const fromRestRequest = transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'request.order.createdDate' },
              { path: 'request.order.updatedDate' },
              { path: 'request.order.purchasedDate' },
              { path: 'request.order.lineItems.digitalFile.expirationDate' },
              {
                path: 'request.order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'request.order.shippingInfo.logistics.deliverByDate' },
              {
                path: 'request.order.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'request.order.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'request.order.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [{ path: 'request.order.lineItems.image' }],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'request.order.lineItems.physicalProperties.weight' },
            ],
          },
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [
              { path: 'request.order.billingInfo.address' },
              {
                path: 'request.order.shippingInfo.logistics.shippingDestination.address',
              },
              {
                path: 'request.order.shippingInfo.logistics.pickupDetails.address',
              },
              { path: 'request.order.recipientInfo.address' },
            ],
          },
        ]);

        return renameKeysFromRESTResponseToSDKResponse(fromRestRequest);
      },
    },
  },

  {
    name: 'getPaymentSettingsForCheckout',
    primaryHttpMappingPath: '/v1/payment-settings-for-checkout',
    transformations: {
      toREST: (payload: any) => {
        const toRestResponse = payload;

        return renameKeysFromSDKRequestToRESTRequest(toRestResponse);
      },
      fromREST: (payload: any) => {
        const fromRestRequest = transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'request.checkout.createdDate' },
              { path: 'request.checkout.updatedDate' },
              {
                path: 'request.checkout.lineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'request.checkout.lineItems.serviceProperties.scheduledDate',
              },
              { path: 'request.checkout.lineItems.serviceProperties.endDate' },
              {
                path: 'request.checkout.shippingInfo.selectedCarrierServiceOption.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'request.checkout.shippingInfo.selectedCarrierServiceOption.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'request.checkout.shippingInfo.carrierServiceOptions.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'request.checkout.shippingInfo.carrierServiceOptions.shippingOptions.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'request.checkout.membershipOptions.eligibleMemberships.expirationDate',
              },
              {
                path: 'request.checkout.membershipOptions.invalidMemberships.membership.expirationDate',
              },
              {
                path: 'request.checkout.subscriptionCharges.charges.cycleBillingDate',
              },
            ],
          },
          {
            transformFn: transformRESTPageURLV2ToSDKPageURLV2,
            paths: [{ path: 'request.checkout.lineItems.url' }],
          },
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [
              { path: 'request.checkout.lineItems.media' },
              {
                path: 'request.checkout.lineItems.catalogOverrideFields.image',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'request.checkout.lineItems.physicalProperties.weight' },
              {
                path: 'request.checkout.lineItems.catalogOverrideFields.physicalProperties.weight',
              },
              {
                path: 'request.checkout.appliedDiscounts.merchantDiscount.percentage',
              },
            ],
          },
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [
              { path: 'request.checkout.billingInfo.address' },
              {
                path: 'request.checkout.shippingInfo.shippingDestination.address',
              },
              {
                path: 'request.checkout.shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.address',
              },
              {
                path: 'request.checkout.shippingInfo.carrierServiceOptions.shippingOptions.logistics.pickupDetails.address',
              },
            ],
          },
        ]);

        return renameKeysFromRESTResponseToSDKResponse(fromRestRequest);
      },
    },
  },
]);
